import { DateTime } from 'luxon'

import { isTimeToBeDefined } from '../../../ts-utils/date-utils'
import type { CivilDate, CivilDateRange, GameLike } from './types'
import { UTC_ZONE, resolveTimeZone, venueLocalDate } from './venueLocalDateTime'

/**
 * The widest zone offsets in use: Kiritimati is UTC+14, Baker Island UTC-12.
 * A civil date in the former starts 14h before the same UTC date; in the latter
 * it ends 12h after. Anything narrower and a padded request would miss games at
 * the edges of the range the user actually asked for.
 */
const MAX_AHEAD_MINUTES = 14 * 60
const MAX_BEHIND_MINUTES = 12 * 60

export const gameTimeZone = (game: GameLike): string =>
  resolveTimeZone(game.venue?.timezone)

/** A game whose time has not been set yet, marked by the 23:58:58Z sentinel. */
export const isGameTimeTbd = (game: GameLike): boolean =>
  isTimeToBeDefined(game.starts_at ?? '')

/**
 * The calendar day a game belongs to, as every surface must agree on it.
 *
 * For a timed game that is the VENUE's local date. For a TBD game there is no
 * local time to speak of — the sentinel encodes the intended day as a UTC date,
 * so re-projecting it into a venue zone would drag it onto the wrong day for
 * any venue EAST of UTC: 23:58:58Z is still 15:58 the same day in Vancouver,
 * but 08:58 the NEXT day in Tokyo.
 */
export const gameDayKey = (game: GameLike): CivilDate => {
  const startsAt = game.starts_at
  if (typeof startsAt !== 'string' || startsAt.trim().length === 0) {
    return ''
  }

  if (isGameTimeTbd(game)) {
    const utc = DateTime.fromISO(startsAt, { zone: UTC_ZONE })
    return utc.isValid ? utc.toFormat('yyyy-MM-dd') : ''
  }

  return venueLocalDate(startsAt, game.venue?.timezone)
}

export interface DayBucket {
  /** `YYYY-MM-DD` in the venue's civil calendar. */
  dayKey: CivilDate
  games: GameLike[]
  /** Games with no time set yet — banded separately, above the timed ones. */
  tbdGames: GameLike[]
}

const startMillis = (game: GameLike): number => {
  const dt = DateTime.fromISO(game.starts_at ?? '', { zone: UTC_ZONE })
  return dt.isValid ? dt.toMillis() : Number.MAX_SAFE_INTEGER
}

/**
 * Groups games into venue-local calendar days, ascending, with TBD games kept
 * as a distinct band rather than sorted in among timed games at 23:58.
 */
export const groupGamesByDay = (games: GameLike[]): DayBucket[] => {
  const byDay = new Map<CivilDate, DayBucket>()

  for (const game of games) {
    const dayKey = gameDayKey(game)
    if (dayKey === '') {
      continue
    }
    let bucket = byDay.get(dayKey)
    if (bucket === undefined) {
      bucket = { dayKey, games: [], tbdGames: [] }
      byDay.set(dayKey, bucket)
    }
    if (isGameTimeTbd(game)) {
      bucket.tbdGames.push(game)
    } else {
      bucket.games.push(game)
    }
  }

  const buckets = Array.from(byDay.values())
  for (const bucket of buckets) {
    bucket.games.sort((a, b) => startMillis(a) - startMillis(b))
  }
  buckets.sort((a, b) =>
    a.dayKey < b.dayKey ? -1 : a.dayKey > b.dayKey ? 1 : 0
  )
  return buckets
}

const isWithinCivilRange = (
  dayKey: CivilDate,
  range: CivilDateRange
): boolean => {
  if (dayKey === '') {
    return false
  }
  //  An absent bound is OPEN-ENDED, not zero-width. Comparing a date string
  //  against a null bound coerces to a number and is always false, which would
  //  quietly empty the view for any one-sided range.
  if (range.start !== null && dayKey < range.start) {
    return false
  }
  if (range.endExclusive !== null && dayKey >= range.endExclusive) {
    return false
  }
  return true
}

/**
 * Keeps only the games whose venue-local day falls inside the window. The API
 * request is padded (see `paddedRequestWindow`), so a raw page always carries
 * games the user did not ask to see; this is what makes the visible set honest.
 */
export const filterGamesByCivilRange = (
  games: GameLike[],
  range: CivilDateRange
): GameLike[] =>
  games.filter((game) => isWithinCivilRange(gameDayKey(game), range))

export interface PaddedWindow {
  /** ISO instant, inclusive. */
  startsAfter: string
  /** ISO instant, exclusive. */
  startsBefore: string
}

/**
 * Widens a civil-date window into the absolute window that can possibly contain
 * it, for ANY venue zone on earth.
 *
 * Asking the API for exactly `[start, endExclusive)` in UTC drops a game at
 * 8pm local in Auckland on the first day of the range and pulls in one at 9pm
 * local in Honolulu on the day before the last. The client then filters the
 * padded superset back down by venue-local day.
 */
export const paddedRequestWindow = (range: CivilDateRange): PaddedWindow => {
  //  Each bound is padded independently, so a one-sided range stays one-sided
  //  rather than becoming no range at all.
  const start =
    range.start === null
      ? null
      : DateTime.fromISO(`${range.start}T00:00:00`, { zone: UTC_ZONE })
  const end =
    range.endExclusive === null
      ? null
      : DateTime.fromISO(`${range.endExclusive}T00:00:00`, { zone: UTC_ZONE })

  return {
    startsAfter:
      start !== null && start.isValid
        ? (start.minus({ minutes: MAX_AHEAD_MINUTES }).toISO() as string)
        : '',
    startsBefore:
      end !== null && end.isValid
        ? (end.plus({ minutes: MAX_BEHIND_MINUTES }).toISO() as string)
        : '',
  }
}

/** Parses `YYYY-MM-DD` bounds as UTC civil dates and half-opens the range. */
export const toCivilDateRange = (
  startsAt?: string | null,
  endsAt?: string | null
): CivilDateRange | null => {
  const start = DateTime.fromISO(String(startsAt ?? ''), { zone: UTC_ZONE })
  const end = DateTime.fromISO(String(endsAt ?? ''), { zone: UTC_ZONE })

  //  Only a range with NEITHER bound is "no range". One-sided is a real filter
  //  the picker can produce, and the legacy request honoured each bound on its
  //  own — collapsing it to null silently widens the query to everything.
  if (!start.isValid && !end.isValid) {
    return null
  }

  return {
    start: start.isValid ? start.toFormat('yyyy-MM-dd') : null,
    //  The picker's `ends_at` is the last day the user wants INCLUDED, so the
    //  exclusive bound is the day after it.
    endExclusive: end.isValid
      ? end.plus({ days: 1 }).toFormat('yyyy-MM-dd')
      : null,
  }
}
